// Which of the three sanctioned gauge captions a month is in (ADR-0038).
//
// The three strings live in `crew/config/plan-language.json` and this file writes none of them: it
// answers WHICH ONE, and the copy stays the copywriter's (section 8). The third state, `gauge_near`,
// has never rendered because its threshold was undecided; `apps/web/src/copy/money.ts` recorded that
// and abstained rather than invent one, which was right. ADR-0038 decided it.
//
// PURE, and deliberately so: no clock, no `Date.now()`, no timezone. The elapsed fraction arrives
// already computed, because the only honest source for it is the same billing period the counts came
// from, and that lives at the read boundary rather than here.
/**
 * The share of the included allowance at which the caption changes to `near`.
 *
 * Chosen against the shipped plans rather than picked round (`0023_plan_seed.sql`): included_units
 * are 12 / 45 / 120, so `ceil(0.8 * n)` fires at 10 / 36 / 96 and leaves runways of 2 / 9 / 24. The
 * entry tier keeps the smallest usable runway instead of a fractional warning.
 */
export const GAUGE_NEAR_USAGE_SHARE = 0.8;
/**
 * How far through the billing period `gauge_near` may still claim "Plenty of time left."
 *
 * THIS IS THE LOAD-BEARING HALF OF THE RULE. `gauge_near` is `gauge_healthy` plus four words, and
 * those words are a CLAIM ABOUT TIME that section 0 rule 4 forbids asserting without backing. The
 * sentence has two defensible readings the register does not disambiguate, so the caption renders
 * only where BOTH are true and falls back to `gauge_healthy`, which asserts nothing about time, past
 * this point. Falling back to the weaker sentence is the honest failure direction.
 */
export const GAUGE_NEAR_MAX_ELAPSED = 0.75;
/**
 * `over` past the allowance, `near` when approaching it with time left, `healthy` otherwise.
 *
 * `used > included` is checked FIRST and independently of the other two, so the over state can never
 * be masked by a bad elapsed fraction: a month that is genuinely over says so whatever the clock did.
 *
 * `used < included` is STRICT. The first draft of ADR-0038 wrote `<=`, which admits `used ===
 * included` and renders "Plenty of time left" beside a runway of ZERO; a panel refuted it by
 * substituting the ADR's own solo row (12 of 12). At exactly the allowance there is no room, so the
 * honest caption is `healthy`, which promises nothing.
 */
export const selectGaugeState = (input) => {
    const { used, included, periodElapsedFraction } = input;
    // Not a real allowance: nothing to be near or over. `healthy` states only what was shipped.
    if (!Number.isFinite(included) || included <= 0)
        return "healthy";
    if (!Number.isFinite(used) || used < 0)
        return "healthy";
    if (used > included)
        return "over";
    // A fraction outside [0,1] is not a period this can reason about, and NaN compares false against
    // everything. Fail to `healthy` rather than to a sentence about time nobody can back.
    const elapsed = periodElapsedFraction;
    if (!Number.isFinite(elapsed) || elapsed < 0 || elapsed > 1)
        return "healthy";
    const threshold = Math.ceil(GAUGE_NEAR_USAGE_SHARE * included);
    const approaching = used >= threshold && used < included;
    return approaching && elapsed <= GAUGE_NEAR_MAX_ELAPSED ? "near" : "healthy";
};
/**
 * The share of a calendar month elapsed at `now`, from a `YYYY-MM` period label.
 *
 * `usage_counters.period` is a MONTH STRING, not a range, and the gauge's counts are read for that
 * exact row, so this is the period the fraction has to describe. There is a second period anchor in
 * the schema, `subscriptions.current_period_end`, which mirrors Stripe's billing period and does NOT
 * align with a calendar month. Using it here would divide a Stripe window by a calendar numerator.
 *
 * Returns null when the label is not a month, when `now` precedes the period, or when the period is
 * already over. A null caller renders `gauge_healthy`, which is the safe direction.
 */
export const monthElapsedFraction = (period, nowMs) => {
    const match = /^(\d{4})-(\d{2})$/.exec(period.trim());
    if (match === null)
        return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    if (!Number.isInteger(year) || month < 1 || month > 12)
        return null;
    if (!Number.isFinite(nowMs))
        return null;
    // UTC throughout: the period label carries no zone, so reading it in local time would move the
    // boundary by the runner's offset and make the same month elapse differently in two places.
    const start = Date.UTC(year, month - 1, 1);
    const end = Date.UTC(month === 12 ? year + 1 : year, month === 12 ? 0 : month, 1);
    if (nowMs < start)
        return null;
    if (nowMs >= end)
        return null;
    return (nowMs - start) / (end - start);
};
//# sourceMappingURL=gauge.js.map